import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, Users, IndianRupee, Eye, CheckCircle2 } from "lucide-react";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const BLUE = "#0B5CFF";
const INK = "#0B0D17";
const MUTED = "#747680";

export default function Analytics() {
  const [webinarId, setWebinarId] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (webinarId) params.set("webinar_id", webinarId);
    params.set("days", days);
    api.get(`/analytics/overview?${params.toString()}`).then((r) => setData(r.data));
  }, [webinarId, days]);

  const funnel = useMemo(() => {
    if (!data) return [];
    const f = data.funnel;
    return [
      { stage: "Visits", value: f.visits, color: "#0B0D17" },
      { stage: "Registered", value: f.registrations, color: "#0B5CFF" },
      { stage: "Paid", value: f.paid, color: "#1E8E3E" },
      { stage: "Attended", value: f.attended, color: "#5AC8FA" },
    ];
  }, [data]);

  const revenueTotal = useMemo(() => (data?.revenue_timeline || []).reduce((s, x) => s + (x.revenue || 0), 0), [data]);
  const conversionRate = useMemo(() => {
    if (!data?.funnel?.visits) return 0;
    return ((data.funnel.registrations / data.funnel.visits) * 100).toFixed(1);
  }, [data]);

  return (
    <div className="space-y-8 vp-fade-up">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="vp-label">Analytics</div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium leading-tight tracking-[-0.025em] mt-1">
            Your webinar business, <span className="font-serif-italic text-[#0B5CFF]">by the numbers.</span>
          </h1>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Funnel, revenue timeline, and source-wise conversion — all in one place.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="vp-input max-w-[240px]" value={webinarId} onChange={(e) => setWebinarId(e.target.value)} data-testid="analytics-webinar-filter">
            <option value="">All webinars</option>
            {(data?.webinars || []).map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
          </select>
          <select className="vp-input max-w-[140px]" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="analytics-days-filter">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Eye} label="Landing visits" value={data?.funnel?.visits ?? 0} tint={INK} />
        <Kpi icon={Users} label="Registrations" value={data?.funnel?.registrations ?? 0} tint={BLUE} />
        <Kpi icon={CheckCircle2} label="Attended" value={data?.funnel?.attended ?? 0} tint="#1E8E3E" />
        <Kpi icon={IndianRupee} label={`Revenue · last ${days}d`} value={fmtINR(revenueTotal)} tint={BLUE} />
      </div>

      {/* Funnel + Conversion */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="vp-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="vp-label">Conversion funnel</div>
              <div className="font-display font-semibold text-xl mt-1">Visit → Register → Pay → Attend</div>
            </div>
            <div className="text-right">
              <div className="vp-label">Visit → Register</div>
              <div className="font-display text-2xl font-medium text-[#0B5CFF]">{conversionRate}%</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={funnel} margin={{ top: 20, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E6EC" vertical={false} />
                <XAxis dataKey="stage" tick={{ fill: MUTED, fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: MUTED, fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(11, 92, 255, 0.05)" }} contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E6EC", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {funnel.map((f, i) => (<Cell key={i} fill={f.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="vp-card p-6">
          <div className="vp-label mb-1">Sources</div>
          <div className="font-display font-semibold text-xl">Where they come from</div>
          <div className="mt-4 space-y-3">
            {(data?.sources || []).length === 0 && <div className="text-sm text-[#747680]">No traffic yet.</div>}
            {(data?.sources || []).slice(0, 6).map((s, i) => {
              const max = data.sources[0]?.count || 1;
              const pct = Math.max(4, Math.round((s.count / max) * 100));
              return (
                <div key={s.source}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#0B0D17] capitalize">{s.source}</span>
                    <span className="text-[#747680]">{s.count} · {fmtINR(s.revenue)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-[#F6F7FA] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0B5CFF] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Revenue timeline */}
      <div className="vp-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="vp-label">Revenue timeline</div>
            <div className="font-display font-semibold text-xl mt-1">Last {days} days</div>
          </div>
          <div className="text-right">
            <div className="vp-label">Total</div>
            <div className="font-display text-2xl font-medium">{fmtINR(revenueTotal)}</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={data?.revenue_timeline || []} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0B5CFF" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#0B5CFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E6EC" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} />
              <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E6EC", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                formatter={(v) => [fmtINR(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#0B5CFF" strokeWidth={2} fill="url(#revFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="vp-kpi">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg border border-[#E4E6EC] flex items-center justify-center" style={{ background: "#F6F7FA" }}>
          <Icon className="w-4 h-4" style={{ color: tint }} strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-4 font-display text-3xl font-medium" style={{ color: tint }}>{value}</div>
      <div className="vp-label mt-1">{label}</div>
    </div>
  );
}
